import { type FC, useEffect, useRef, useState } from 'react';

import { type User } from '@/types';
import {
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Typography,
} from '@/ui';

import { useUpdateUserTranscriptSharingMutation } from '@/store/api/userApi';
import { isAdmin } from '@/types/user';

interface TranscriptSharingProps {
  currentUser: User;
  member: User;
  onUpdated: () => void;
}

const TranscriptSharing: FC<TranscriptSharingProps> = ({
  currentUser,
  member,
  onUpdated,
}) => {
  const canEdit =
    isAdmin(currentUser) ||
    member.coaches.some((coach) => coach.id === currentUser.id);

  const [updateTranscriptSharing, { isLoading }] =
    useUpdateUserTranscriptSharingMutation();

  const [enabled, setEnabled] = useState(!!member.autoShareSessionTranscripts);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const currentMemberIdRef = useRef(member.id);
  const requestIdRef = useRef(0);

  useEffect(() => {
    currentMemberIdRef.current = member.id;
    requestIdRef.current += 1;
    setEnabled(!!member.autoShareSessionTranscripts);
    setErrorMessage(null);
  }, [member.autoShareSessionTranscripts, member.id]);

  if (!canEdit) return null;

  const onChange = async (nextEnabled: boolean) => {
    const memberId = member.id;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setErrorMessage(null);
    setEnabled(nextEnabled);
    try {
      await updateTranscriptSharing({
        enabled: nextEnabled,
        id: memberId,
      }).unwrap();

      if (
        currentMemberIdRef.current !== memberId ||
        requestIdRef.current !== requestId
      ) {
        return;
      }
      onUpdated();
    } catch {
      if (
        currentMemberIdRef.current !== memberId ||
        requestIdRef.current !== requestId
      ) {
        return;
      }
      setEnabled(!nextEnabled);
      setErrorMessage('Could not update transcript sharing. Please try again.');
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography className="mb-2" variant="subtitle1">
          Transcripts
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              color="primary"
              disabled={isLoading}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={`Share session transcripts with ${member.firstName}`}
        />
        {errorMessage && (
          <Alert className="mt-3" severity="error">
            {errorMessage}
          </Alert>
        )}
        <Typography className="text-cement-400" variant="body2">
          New coaching session transcripts will be shared in Google Docs with{' '}
          {member.documentsEmail ?? member.email}. <br />
          <br />
          Please check with your member before turning this on.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TranscriptSharing;
